import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';

// data fetching is handled by hooks
import StudentRow from './StudentRow';
import AssessmentHeader from './AssessmentHeader';
import AssessmentScoringControls from './AssessmentScoringControls';
import { todayTR } from './assessmentUiUtils';
import useLearningActivityAssessmentSession from './useLearningActivityAssessmentSession';
import useMaarifBooks from './useMaarifBooks';
import useMaarifLearningAreas from './useMaarifLearningAreas';
import useMaarifLearningActivities from './useMaarifLearningActivities';
import useMaarifAssessments from './useMaarifAssessments';
import useMaarifInitLists from './useMaarifInitLists';
import AssessmentTableView from './AssessmentTableView';
import AssessmentTableViewV2 from './AssessmentTableViewV2';


const LearningOutcomeAssessmentPage = ({ onSuccess, onError, schoolId }) => {
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedLearningArea, setSelectedLearningArea] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const [assessedOn, setAssessedOn] = useState(todayTR());

  const [bulkCriterion, setBulkCriterion] = useState(null);
  const [bulkScore, setBulkScore] = useState(null);

  // Optional UI focus: show only one criterion across all students
  const [focusCriterion, setFocusCriterion] = useState(null);

  const [viewMode, setViewMode] = useState('table');
  const [tableFullscreenOpen, setTableFullscreenOpen] = useState(false);

  // itemsByKey: { [`${studentId}:${criterionId}`]: { score } }
  // stored in session hook

  // session state is inside the hook

  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  const notifyError = useCallback((msg) => { onErrorRef.current && onErrorRef.current(msg); }, []);
  const notifySuccess = useCallback((msg) => { onSuccessRef.current && onSuccessRef.current(msg); }, []);

  const { classes, courses, loadingInit } = useMaarifInitLists({ schoolId, notifyError });

  const { books, loadingBooks } = useMaarifBooks({
    schoolId,
    lessonId: selectedCourse?.id,
    gradeLevel: selectedClass?.grade_level,
    notifyError,
  });

  const { learningAreas, loadingAreas } = useMaarifLearningAreas({
    bookId: selectedBook?.id,
    notifyError,
  });

  const { activities, loadingActivities } = useMaarifLearningActivities({
    learningAreaId: selectedLearningArea?.id,
    notifyError,
  });

  const { assessments, loadingAssessments } = useMaarifAssessments({
    activityId: selectedActivity?.id,
    notifyError,
  });

  const prevActivityIdRef = useRef(selectedActivity?.id ?? null);
  const activityChanged = prevActivityIdRef.current !== (selectedActivity?.id ?? null);
  useEffect(() => {
    prevActivityIdRef.current = selectedActivity?.id ?? null;
  }, [selectedActivity?.id]);

  const assessmentsReady = useMemo(() => {
    if (!selectedActivity?.id) return true;
    if (activityChanged) return false;
    return !loadingAssessments;
  }, [selectedActivity?.id, activityChanged, loadingAssessments]);

  const effectiveAssessmentId = useMemo(() => {
    if (loadingAssessments) return null;
    const list = Array.isArray(assessments) ? assessments : [];
    if (list.length === 1) return list[0]?.id ?? null;
    return selectedAssessment?.id ?? null;
  }, [loadingAssessments, assessments, selectedAssessment?.id]);

  const {
    sessionId,
    students,
    criteria,
    bands,
    itemsByKey,
    progress,
    loadingSession,
    saving,
    savingBulk,
    isSavingScore,
    setScoreForStudentCriterion,
    applyScoreToAll: applyScoreToAllInSession,
    fillUnsetScoresForCriterion,
    resetSessionState,
  } = useLearningActivityAssessmentSession({
    schoolId,
    classId: selectedClass?.id,
    learningActivityId: selectedActivity?.id,
    assessedOn,
    assessmentId: effectiveAssessmentId,
    enabled: !!selectedClass
      && !!selectedCourse
      && !!selectedBook
      && !!selectedLearningArea
      && !!selectedActivity
      && !!assessedOn
      && assessmentsReady
      && ((assessments || []).length === 0 || (assessments || []).length === 1 || !!effectiveAssessmentId),
    notifyError,
    notifySuccess,
  });

  // Keep selected class/course valid when option lists change
  useEffect(() => {
    setSelectedClass((prev) => {
      if (!prev?.id) return classes[0] || null;
      const stillThere = (classes || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || (classes[0] || null);
    });
  }, [classes]);

  useEffect(() => {
    setSelectedCourse((prev) => {
      if (!prev?.id) return courses[0] || null;
      const stillThere = (courses || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || (courses[0] || null);
    });
  }, [courses]);

  // Reset assessment state when selection context changes
  useEffect(() => {
    resetSessionState();
    setFocusCriterion(null);
  }, [resetSessionState, schoolId, selectedClass?.id, selectedCourse?.id, selectedBook?.id, selectedLearningArea?.id, selectedActivity?.id, assessedOn]);

  // Selection cascade reset (UI state) when upstream selections change
  useEffect(() => {
    setSelectedBook(null);
    setSelectedLearningArea(null);
    setSelectedActivity(null);
    setSelectedAssessment(null);
  }, [schoolId, selectedCourse?.id, selectedClass?.grade_level]);

  useEffect(() => {
    setSelectedBook((prev) => {
      if (!prev?.id) return books[0] || null;
      const stillThere = (books || []).find((b) => String(b?.id) === String(prev.id));
      return stillThere || (books[0] || null);
    });
  }, [books]);

  useEffect(() => {
    setSelectedLearningArea(null);
    setSelectedActivity(null);
    setSelectedAssessment(null);
  }, [selectedBook?.id]);

  useEffect(() => {
    setSelectedLearningArea((prev) => {
      if (!prev?.id) return learningAreas[0] || null;
      const stillThere = (learningAreas || []).find((a) => String(a?.id) === String(prev.id));
      return stillThere || (learningAreas[0] || null);
    });
  }, [learningAreas]);

  useEffect(() => {
    setSelectedActivity(null);
    setSelectedAssessment(null);
  }, [selectedLearningArea?.id]);

  useEffect(() => {
    setSelectedActivity((prev) => {
      if (!prev?.id) return activities[0] || null;
      const stillThere = (activities || []).find((a) => String(a?.id) === String(prev.id));
      return stillThere || (activities[0] || null);
    });
  }, [activities]);

  useEffect(() => {
    setSelectedAssessment(null);
  }, [selectedActivity?.id]);

  useEffect(() => {
    setSelectedAssessment((prev) => {
      if (!assessments || assessments.length === 0) return null;
      if (assessments.length === 1) return assessments[0];
      if (prev?.id) {
        const stillThere = (assessments || []).find((a) => String(a?.id) === String(prev.id));
        if (stillThere) return stillThere;
      }
      return assessments[0];
    });
  }, [assessments]);

  // UI-only progress (instant update) based on local scores; avoids extra progress calls
  const uiProgress = useMemo(() => {
    if (!sessionId) return null;

    const st = Array.isArray(students) ? students : [];
    const crit = Array.isArray(criteria) ? criteria : [];
    const criterionIds = crit.filter((c) => c && c.id != null).map((c) => String(c.id));
    if (st.length === 0 || criterionIds.length === 0) return null;
    const totalExpected = st.length * criterionIds.length;

    let recordedCount = 0;
    for (const s of st) {
      if (!s || s.id == null) continue;
      const sid = String(s.id);
      for (const cid of criterionIds) {
        const k = `${sid}:${cid}`;
        const score = itemsByKey?.[String(k)]?.score;
        const hasScore = score !== null && score !== undefined && score !== '' && Number.isFinite(Number(score));
        if (hasScore) recordedCount += 1;
      }
    }

    const missingCount = Math.max(0, totalExpected - recordedCount);
    return {
      missing_count: missingCount,
      recorded_count: recordedCount,
      total_expected: totalExpected,
      can_publish: false,
    };
  }, [sessionId, students, criteria, itemsByKey]);

  const displayProgress = uiProgress || progress;

  const pageLoading = loadingInit
    || loadingBooks
    || loadingAreas
    || loadingActivities
    || loadingAssessments
    || loadingSession;

  // Keep bulk selection valid when criteria change
  useEffect(() => {
    if (!criteria || criteria.length === 0) {
      setBulkCriterion(null);
      setBulkScore(null);
      return;
    }
    setBulkCriterion((prev) => {
      if (!prev?.id) return null;
      const stillThere = (criteria || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || null;
    });
  }, [criteria]);

  // Keep focus selection valid when criteria change
  useEffect(() => {
    if (!criteria || criteria.length === 0) {
      setFocusCriterion(null);
      return;
    }
    setFocusCriterion((prev) => {
      if (!prev?.id) return null;
      const stillThere = (criteria || []).find((c) => String(c?.id) === String(prev.id));
      return stillThere || null;
    });
  }, [criteria]);

  const sortedCriteria = useMemo(
    () => (criteria || []).slice().sort((a, b) => (Number(a?.sort_order || 0) - Number(b?.sort_order || 0))),
    [criteria]
  );

  const applyScoreToAll = useCallback(async () => {
    return applyScoreToAllInSession({
      criterionId: bulkCriterion?.id,
      score: bulkScore,
    });
  }, [applyScoreToAllInSession, bulkCriterion?.id, bulkScore]);

  const computedResultsByStudentId = useMemo(() => {
    const st = Array.isArray(students) ? students : [];
    const crit = Array.isArray(criteria) ? criteria : [];
    const b = Array.isArray(bands) ? bands : [];

    const criterionIds = crit.filter((c) => c && c.id != null).map((c) => String(c.id));
    const sorted = b.slice().sort((x, y) => Number(x?.sort_order || 0) - Number(y?.sort_order || 0));

    const pickBand = (total) => {
      const totalNum = (total === null || total === undefined || total === '' ? null : Number(total));
      if (!Number.isFinite(totalNum)) return { band_id: null, band_label: null };

      for (const band of sorted) {
        const min = band?.min_score;
        const max = band?.max_score;
        const minOk = (min === null || min === undefined) ? true : totalNum >= Number(min);
        const maxOk = (max === null || max === undefined) ? true : totalNum <= Number(max);
        if (minOk && maxOk) {
          return { band_id: band?.id ?? null, band_label: band?.label ?? null };
        }
      }
      return { band_id: null, band_label: null };
    };

    const out = {};
    for (const s of st) {
      if (!s || s.id == null) continue;
      const sid = String(s.id);
      let total = 0;
      for (const cid of criterionIds) {
        const k = `${sid}:${cid}`;
        const score = itemsByKey?.[String(k)]?.score;
        const n = Number(score);
        if (Number.isFinite(n)) total += n;
      }
      const band = pickBand(total);
      out[sid] = {
        student_id: Number(s.id),
        total_score: total,
        band_id: band.band_id,
        band_label: band.band_label,
      };
    }
    return out;
  }, [students, criteria, bands, itemsByKey]);

  const bandCounts = useMemo(() => {
    const counts = {};
    (bands || []).forEach((b) => {
      if (!b) return;
      const k = String(b.id);
      counts[k] = 0;
    });
    Object.values(computedResultsByStudentId || {}).forEach((r) => {
      const k = r?.band_id != null ? String(r.band_id) : '__none__';
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [bands, computedResultsByStudentId]);

  const sortedBands = useMemo(
    () => (bands || []).slice().sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0)),
    [bands]
  );

  const headerChips = useMemo(() => {
    const chips = [];
    if (displayProgress) {
      chips.push(
        <Chip
          key="p1"
          size="small"
          label={`Eksik: ${displayProgress.missing_count}`}
          color={displayProgress.missing_count === 0 ? 'success' : 'warning'}
        />
      );
      chips.push(
        <Chip
          key="p2"
          size="small"
          label={`Kayıt: ${displayProgress.recorded_count}/${displayProgress.total_expected}`}
        />
      );
    }
    return chips;
  }, [displayProgress]);

  return (
    <>
      {pageLoading && <LinearProgress sx={{ mb: 2 }} />}
      <AssessmentHeader
        classes={classes}
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        courses={courses}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        books={books}
        selectedBook={selectedBook}
        setSelectedBook={setSelectedBook}
        learningAreas={learningAreas}
        selectedLearningArea={selectedLearningArea}
        setSelectedLearningArea={setSelectedLearningArea}
        activities={activities}
        selectedActivity={selectedActivity}
        setSelectedActivity={setSelectedActivity}
        assessments={assessments}
        selectedAssessment={selectedAssessment}
        setSelectedAssessment={setSelectedAssessment}
        assessedOn={assessedOn}
        setAssessedOn={setAssessedOn}
        headerChips={headerChips}
        loadingInit={loadingInit}
        loadingBooks={loadingBooks}
        loadingAreas={loadingAreas}
        loadingActivities={loadingActivities}
        loadingSession={loadingSession}
        saving={saving}
      />

      {!sessionId ? (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          {pageLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Yükleniyor...
              </Typography>
            </Stack>
          ) : (
            <Typography color="text.secondary">
              Şube, ders, kitap, öğrenme alanı, etkinlik ve tarih seçin.
            </Typography>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Öğrenme Etkinliği Değerlendirme
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, wordBreak: 'break-word' }}>
                {selectedActivity?.name || '—'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          <AssessmentScoringControls
            bands={bands}
            sortedBands={sortedBands}
            bandCounts={bandCounts}
            readOnly={false}
            sortedCriteria={sortedCriteria}
            bulkCriterion={bulkCriterion}
            setBulkCriterion={setBulkCriterion}
            bulkScore={bulkScore}
            setBulkScore={setBulkScore}
            applyScoreToAll={applyScoreToAll}
            saving={saving}
            focusCriterion={focusCriterion}
            setFocusCriterion={setFocusCriterion}
          />

          <Tabs
            value={viewMode}
            onChange={(_, v) => setViewMode(v)}
            sx={{ mb: 1 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="list" label="Liste" />
            <Tab value="table" label="Tablo" />
            <Tab value="list2" label="LISTE2" />
          </Tabs>

          <Dialog
            fullScreen
            open={tableFullscreenOpen}
            onClose={() => setTableFullscreenOpen(false)}
          >
            <AppBar position="sticky" color="default" elevation={1}>
              <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>
                  Tablo — {selectedActivity?.name || ''}
                </Typography>
                <IconButton edge="end" onClick={() => setTableFullscreenOpen(false)} aria-label="Kapat">
                  <CloseIcon />
                </IconButton>
              </Toolbar>
            </AppBar>

            <Box sx={{ p: 1.5 }}>
              <AssessmentTableView
                students={students}
                sortedCriteria={sortedCriteria}
                focusedCriterionId={focusCriterion?.id || null}
                itemsByKey={itemsByKey}
                onSetScore={setScoreForStudentCriterion}
                onFillUnsetScores={fillUnsetScoresForCriterion}
                readOnly={false}
                savingBulk={savingBulk}
                isSavingScore={isSavingScore}
                bands={bands}
                containerSx={{
                  border: 'none',
                  borderRadius: 0,
                  maxHeight: 'calc(100vh - 64px - 24px)',
                  overflowY: 'auto',
                }}
              />
            </Box>
          </Dialog>

          {viewMode === 'list2' ? (
            <AssessmentTableViewV2
              students={students}
              sortedCriteria={sortedCriteria}
              focusedCriterionId={focusCriterion?.id || null}
              itemsByKey={itemsByKey}
              onSetScore={setScoreForStudentCriterion}
                readOnly={false}
              isSavingScore={isSavingScore}
              bands={bands}
              forceMobileLayout
            />
          ) : viewMode === 'table' ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setTableFullscreenOpen(true)}
                  aria-label="Tam ekran"
                  title="Tam ekran"
                >
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Box>

              <AssessmentTableView
                students={students}
                sortedCriteria={sortedCriteria}
                focusedCriterionId={focusCriterion?.id || null}
                itemsByKey={itemsByKey}
                onSetScore={setScoreForStudentCriterion}
                onFillUnsetScores={fillUnsetScoresForCriterion}
                readOnly={false}
                savingBulk={savingBulk}
                isSavingScore={isSavingScore}
                bands={bands}
                containerSx={{ maxHeight: '70vh', overflowY: 'auto' }}
              />
            </>
          ) : (
            <List disablePadding>
              {(students || []).map((st) => (
                <StudentRow
                  key={st.id}
                  student={st}
                  sortedCriteria={sortedCriteria}
                  focusedCriterionId={focusCriterion?.id || null}
                  result={computedResultsByStudentId[String(st.id)] || null}
                  itemsByKey={itemsByKey}
                  onSetScore={setScoreForStudentCriterion}
                  readOnly={false}
                  isSavingScore={isSavingScore}
                  bands={bands}
                />
              ))}
            </List>
          )}

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              mt: 2,
              pt: 1,
              pb: 1,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {displayProgress ? `Kayıt: ${displayProgress.recorded_count}/${displayProgress.total_expected} — Eksik: ${displayProgress.missing_count}` : ''}
            </Typography>
            <Box />
          </Box>
        </Paper>
      )}
    </>
  );
};

export default LearningOutcomeAssessmentPage;
